import { formatDateNonlocalised } from '../../../bootstrap/localisation';
import {
  SELECT_DAY,
  SELECT_DAY_FOR_HOTELS,
  SELECT_MONTH,
  HOTEL_SELECT_MONTH,
  SELECT_WHOLE_YEAR,
  SET_IS_RETURN,
  SET_HAS_HOTEL_DATA_ON_BOTH_DATES,
} from '../helpers/constants/actionsConstants';
import * as constants from '../helpers/constants/dataTypeConstants';
import {
  getHotelsDatesData,
  getSelectedHotelCheckInDate,
  getSelectedHotelCheckOutDate,
} from '../selectors';

import type * as analyticsConstants from '../helpers/constants/analyticsConstants';
import type { CheapestCombination } from '../selectors/cheapestDatesSelectors';
import type { Direction, State, TripDuration } from '../selectors/types';

export type DateSelectDaySource =
  typeof constants.GRID_SOURCE | typeof constants.AUTOMATED_SOURCE;

export type VerticalType =
  | typeof analyticsConstants.FLIGHT_VERTICAL
  | typeof analyticsConstants.HOTEL_VERTICAL;

const selectDay = (
  dayOfMonth: number,
  direction: Direction,
  source: string,
) => ({
  type: SELECT_DAY,
  payload: {
    dayOfMonth, // 1 - 31
    direction, // outbound or inbound
    source, // Grid, Automated
  },
  meta: { googleAnalyticsLogEvent: SELECT_DAY },
});

const selectMonth = (
  month: number,
  year: number,
  direction: Direction,
  source: string,
) => ({
  type: SELECT_MONTH,
  payload: { direction, month, year, source },
  meta: { googleAnalyticsLogEvent: SELECT_MONTH },
});

const selectMonthForHotel = (
  month: number,
  year: number,
  direction: Direction,
  source: string,
) => ({
  type: HOTEL_SELECT_MONTH,
  payload: {
    month, // 1 - 12
    year, // yyyy
    direction, // outbound or inbound
    source, // Previous, Next, Select, Automated
  },
});

const selectDayForHotel = (
  dayOfMonth: number,
  direction: Direction,
  source: DateSelectDaySource,
) => ({
  type: SELECT_DAY_FOR_HOTELS,
  payload: {
    dayOfMonth, // 1 - 31
    direction, // outbound or inbound
    source, // Grid, Automated
  },
});

const setHasHotelDataOnBothDates = (hasData: boolean) => ({
  type: SET_HAS_HOTEL_DATA_ON_BOTH_DATES,
  payload: hasData,
});

const selectOutboundMonth = (month: number, year: number, source: string) =>
  selectMonth(month, year, constants.OUTBOUND_DIRECTION, source);

const selectInboundMonth = (month: number, year: number, source: string) =>
  selectMonth(month, year, constants.INBOUND_DIRECTION, source);

const selectCheckOutMonthForHotels = (
  month: number,
  year: number,
  source: string,
) => selectMonthForHotel(month, year, constants.INBOUND_DIRECTION, source);

const selectCheckInMonthForHotels = (
  month: number,
  year: number,
  source: string,
) => selectMonthForHotel(month, year, constants.OUTBOUND_DIRECTION, source);

const selectWholeYear = (wholeYear: boolean) => ({
  type: SELECT_WHOLE_YEAR,
  payload: wholeYear,
});

const setIsReturn = (isReturn: boolean) => ({
  type: SET_IS_RETURN,
  payload: isReturn,
});

const hasHotelDataOnBothDates = (state: State) => {
  const hasDateData = (date: Date) => {
    const formattedDate = formatDateNonlocalised(date);
    return getHotelsDatesData(state)[formattedDate] !== undefined;
  };

  const checkInState = getSelectedHotelCheckInDate(state);
  const checkOutState = getSelectedHotelCheckOutDate(state);
  const checkInDateHasPrice = !!checkInState && hasDateData(checkInState);
  const checkOutDateHasPrice = !!checkOutState && hasDateData(checkOutState);

  return setHasHotelDataOnBothDates(
    checkInDateHasPrice && checkOutDateHasPrice,
  );
};

export const setCheapestCombinations = (
  combinations: Record<TripDuration, CheapestCombination | null>,
) => ({
  type: 'SET_CHEAPEST_COMBINATIONS',
  payload: combinations,
});

export {
  selectDay,
  selectMonth,
  selectDayForHotel,
  selectMonthForHotel,
  selectOutboundMonth,
  selectInboundMonth,
  selectCheckOutMonthForHotels,
  selectCheckInMonthForHotels,
  selectWholeYear,
  setIsReturn,
  hasHotelDataOnBothDates,
  setHasHotelDataOnBothDates,
};
